import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import './App.css';
import LandingPage from './components/LandingPage';
import api from './services/api';

const Login = lazy(() => import('./components/Login'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const SuperAdminDashboard = lazy(() => import('./components/SuperAdminDashboard'));
const AuditLogPage = lazy(() => import('./components/AuditLogPage'));
const SuperAdminLogin = lazy(() => import('./components/SuperAdminLogin'));
const ForcePasswordReset = lazy(() => import('./components/ForcePasswordReset'));
const CompanySetupWizard = lazy(() => import('./components/CompanySetupWizard'));

function AppLoading({ message = 'Loading...' }) {
  return (
    <div className="App">
      <div className="loading" role="status" aria-live="polite">
        <span className="app-spinner" aria-hidden="true" />
        <span>{message}</span>
      </div>
    </div>
  );
}

function App() {
  const pathname = window.location.pathname;
  const initialUser = api.getCurrentUser();
  const hasStoredSession = Boolean(
    initialUser
    || api.getToken()
    || api.getRefreshToken()
    || localStorage.getItem('impersonation_token')
  );

  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(hasStoredSession);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    let isMounted = true;
    const bootstrapSession = async () => {
      if (!hasStoredSession) {
        if (isMounted) setLoading(false);
        return;
      }
      const currentUser = await api.restoreSession();
      if (!isMounted) return;
      setUser(currentUser);
      setLoading(false);
    };
    bootstrapSession();
    return () => {
      isMounted = false;
    };
  }, [hasStoredSession]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Prevent mouse-wheel increments on focused number inputs while still allowing page scroll.
    const handleWheel = (event) => {
      const activeElement = document.activeElement;
      if (!(activeElement instanceof HTMLInputElement) || activeElement.type !== 'number') return;
      const eventTarget = event.target;
      if (eventTarget instanceof Node && !activeElement.contains(eventTarget)) return;
      activeElement.blur();
    };

    document.addEventListener('wheel', handleWheel, { capture: true, passive: true });
    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleUserUpdate = useCallback((userData) => {
    setUser(userData);
  }, []);

  const handleLogout = useCallback(() => {
    api.logout();
    setUser(null);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      handleLogout();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    };
    const syncAuthState = () => {
      setUser(api.getCurrentUser());
    };
    const handleStorage = (event) => {
      if (!['token', 'refresh_token', 'user'].includes(event.key || '')) return;
      syncAuthState();
    };
    window.addEventListener('app:auth-expired', handleAuthExpired);
    window.addEventListener('app:auth-changed', syncAuthState);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('app:auth-expired', handleAuthExpired);
      window.removeEventListener('app:auth-changed', syncAuthState);
      window.removeEventListener('storage', handleStorage);
    };
  }, [handleLogout]);

  if (loading) {
    return <AppLoading />;
  }

  const isImpersonateRoute = pathname.startsWith('/impersonate');
  if (isImpersonateRoute) {
    const pendingToken = localStorage.getItem('impersonation_token');
    const pendingRefreshToken = localStorage.getItem('impersonation_refresh_token');
    const pendingUser = localStorage.getItem('impersonation_user');
    if (pendingToken && pendingUser) {
      let parsedUser = null;
      try {
        parsedUser = JSON.parse(pendingUser);
      } catch (_err) {
        parsedUser = null;
      }
      api.setAuthSession({
        token: pendingToken,
        refreshToken: pendingRefreshToken,
        user: parsedUser
      });
      localStorage.removeItem('impersonation_token');
      localStorage.removeItem('impersonation_refresh_token');
      localStorage.removeItem('impersonation_user');
      window.location.replace('/');
      return null;
    }
  }

  const isLoginRoute = pathname === '/login';
  const isSetupRoute = pathname.startsWith('/superadmin/setup');
  const isAuditRoute = pathname.startsWith('/superadmin/audit');
  const isSuperAdminRoute = pathname.startsWith('/superadmin');

  const renderDeferred = (node) => (
    <Suspense fallback={<AppLoading />}>
      {node}
    </Suspense>
  );

  if (user && user.force_password_reset) {
    return renderDeferred(<ForcePasswordReset onComplete={setUser} />);
  }

  return (
    <div className="App">
      {!isOnline && (
        <div className="offline-banner" role="status" aria-live="polite">
          System is offline. We’ll save your changes and try to sync when the connection returns.
        </div>
      )}
      {isSetupRoute ? (
        !user ? (
          renderDeferred(<SuperAdminLogin onLogin={handleLogin} />)
        ) : user.is_super_admin ? (
          renderDeferred(<CompanySetupWizard onLogout={handleLogout} />)
        ) : (
          <div className="loading">
            Access denied.
            <button type="button" onClick={handleLogout} style={{ marginLeft: 12 }}>
              Logout
            </button>
          </div>
        )
      ) : isAuditRoute ? (
        !user ? (
          renderDeferred(<SuperAdminLogin onLogin={handleLogin} />)
        ) : user.is_super_admin ? (
          renderDeferred(<AuditLogPage onLogout={handleLogout} />)
        ) : (
          <div className="loading">
            Access denied.
            <button type="button" onClick={handleLogout} style={{ marginLeft: 12 }}>
              Logout
            </button>
          </div>
        )
      ) : isSuperAdminRoute ? (
        !user ? (
          renderDeferred(<SuperAdminLogin onLogin={handleLogin} />)
        ) : user.is_super_admin ? (
          renderDeferred(<SuperAdminDashboard user={user} onLogout={handleLogout} />)
        ) : (
          <div className="loading">
            Access denied.
            <button type="button" onClick={handleLogout} style={{ marginLeft: 12 }}>
              Logout
            </button>
          </div>
        )
      ) : !user ? (
        isLoginRoute ? renderDeferred(<Login onLogin={handleLogin} />) : <LandingPage />
      ) : user.role === 'admin' || user.role === 'owner' || user.role === 'superadmin' ? (
        user.is_super_admin ? (
          renderDeferred(<SuperAdminDashboard user={user} onLogout={handleLogout} />)
        ) : (
          renderDeferred(<AdminDashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />)
        )
      ) : (
        // Every non-admin/owner account (custom roles, and legacy 'user' tokens still in their
        // refresh grace window) renders the permission-gated dashboard; the old hardcoded
        // UserDashboard has been retired in favour of the configurable Operator role.
        renderDeferred(<AdminDashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />)
      )}
    </div>
  );
}

export default App;
